import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import type { InputRef } from 'antd';
import { Button, Flex, Input, Spin, Typography } from 'antd';

import Modal from '/ui/Modal';

import filesActions from '../../../actions';

import { message } from '../../../../../utils/message';

import { AppDispatch } from '../../../../../types/app';
import { AiFileItem } from '../../../types';

const { Text } = Typography;

type Props = {
  visible: boolean;
  onClose: () => void;
  onGenerated: (file: AiFileItem) => void;
};

const GenerateAtlasModal = ({ visible, onClose, onGenerated }: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const inputRef = useRef<InputRef>(null);
  const [fileName, setFileName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      setFileName('');
      setError(null);
      return;
    }

    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [visible]);

  const handleGenerate = useCallback(async () => {
    const trimmedName = fileName.trim();

    if (generating) return;

    if (!trimmedName || trimmedName.length > 255) {
      setError(
        intl.formatMessage({ id: 'files.atlasGenerateModal.errorName' })
      );
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      const file = await dispatch(
        filesActions.Files.addEmptyAtlasFile(trimmedName)
      );

      onClose();
      onGenerated(file);
    } catch (err) {
      console.error(err);
      message.error('files.atlasGenerate.error');
      setError(
        intl.formatMessage({ id: 'files.atlasGenerateModal.errorGenerate' })
      );
    } finally {
      setGenerating(false);
    }
  }, [dispatch, fileName, generating, intl, onClose, onGenerated]);

  return (
    <Modal
      className="files-page__atlas-generate-modal"
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnHidden
      width={420}
      mask={{
        closable: !generating,
      }}
    >
      <Spin spinning={generating}>
        <Flex className="files-page__atlas-generate-inner" vertical gap={16}>
          <Text className="files-page__atlas-generate-title">
            <FormattedMessage id="files.atlasGenerateModal.title" />
          </Text>
          <Input
            ref={inputRef}
            value={fileName}
            maxLength={255}
            disabled={generating}
            placeholder={intl.formatMessage({
              id: 'files.atlasGenerateModal.namePlaceholder',
            })}
            onChange={(event) => setFileName(event.target.value)}
            onPressEnter={() => void handleGenerate()}
          />
          {error && <Text type="danger">{error}</Text>}
          <Button
            type="primary"
            className="files-page__atlas-generate-btn"
            loading={generating}
            disabled={generating}
            onClick={() => void handleGenerate()}
          >
            <FormattedMessage id="files.atlasGenerateModal.generate" />
          </Button>
        </Flex>
      </Spin>
    </Modal>
  );
};

export default GenerateAtlasModal;
